import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, Card, CardContent, Button, CircularProgress, Stack, Alert, Chip } from '@mui/material';
import AdminSidebar from './components/AdminSidebar';
import { adminApi } from '../../api/adminApi';

interface ModerationLabel {
    name: string;
    confidence: number;
    parentName: string;
}

interface PendingVideo {
    userId: string;
    name: string;
    submittedAt: string;
    reviewUrl: string | null;
    moderationLabels: ModerationLabel[] | null;
}

/**
 * Minimal intro-video moderation queue: lists profiles with a pending video,
 * plays each via a short-lived signed URL, and approves/rejects via the
 * /api/admin/videos endpoint. Approve moves the clip to the public bucket and
 * flips video_status to 'approved' (which is what surfaces it in Daily Picks).
 */
const AdminVideosScreen: React.FC = () => {
    const [items, setItems] = useState<PendingVideo[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [actingOn, setActingOn] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await adminApi.getPendingVideos();
            setItems(res.pending || []);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to load pending videos');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const moderate = async (userId: string, action: 'approve' | 'reject') => {
        setActingOn(userId);
        setError(null);
        try {
            await adminApi.moderateVideo(userId, action);
            setItems((prev) => prev.filter((i) => i.userId !== userId));
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Action failed');
        } finally {
            setActingOn(null);
        }
    };

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <AdminSidebar />
            <Box component="main" sx={{ flexGrow: 1, p: 3, minWidth: 0 }}>
                <Typography variant="h4" gutterBottom>Pending Intro Videos</Typography>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {loading ? (
                    <CircularProgress />
                ) : items.length === 0 ? (
                    <Typography color="text.secondary">No videos awaiting review.</Typography>
                ) : (
                    <Stack spacing={2}>
                        {items.map((v) => (
                            <Card key={v.userId} variant="outlined">
                                <CardContent>
                                    <Typography variant="h6">{v.name || v.userId}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Submitted {new Date(v.submittedAt).toLocaleString()}
                                    </Typography>
                                    {v.moderationLabels && v.moderationLabels.length > 0 ? (
                                        <Box sx={{ mt: 1 }}>
                                            <Typography variant="body2" color="error" sx={{ fontWeight: 600, mb: 0.5 }}>
                                                Auto-flagged for:
                                            </Typography>
                                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                {v.moderationLabels.map((l) => (
                                                    <Chip
                                                        key={l.name}
                                                        label={l.confidence > 0 ? `${l.name} ${l.confidence}%` : l.name}
                                                        color="error"
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                ))}
                                            </Stack>
                                        </Box>
                                    ) : (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                            Awaiting auto-moderation, or cleared for manual review.
                                        </Typography>
                                    )}
                                    <Box sx={{ my: 2 }}>
                                        {v.reviewUrl ? (
                                            <video
                                                src={v.reviewUrl}
                                                controls
                                                playsInline
                                                style={{ maxWidth: 320, maxHeight: 480, borderRadius: 8, background: '#000' }}
                                            />
                                        ) : (
                                            <Typography color="error">Video file missing.</Typography>
                                        )}
                                    </Box>
                                    <Stack direction="row" spacing={2}>
                                        <Button
                                            variant="contained"
                                            color="success"
                                            disabled={actingOn === v.userId}
                                            onClick={() => moderate(v.userId, 'approve')}
                                        >
                                            Approve
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            disabled={actingOn === v.userId}
                                            onClick={() => moderate(v.userId, 'reject')}
                                        >
                                            Reject
                                        </Button>
                                    </Stack>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>
                )}
            </Box>
        </Box>
    );
};

export default AdminVideosScreen;
